const SOUND_SETTING_KEY = 'kelimoSoundEnabled';
const SOUND_VOLUME_KEY = 'kelimoSoundVolume';
let sharedContext = null;
let lastErrorSoundAt = 0;

// A trusted click/key press can unlock audio before a later WebSocket event.
export function unlockSoundFromGesture() {
  if (!isSoundEnabled() || typeof window === 'undefined') return;
  const AudioContext = window.AudioContext || window.webkitAudioContext;
  if (!AudioContext) return;
  try {
    if (!sharedContext || sharedContext.state === 'closed') sharedContext = new AudioContext();
    if (['suspended','interrupted'].includes(sharedContext.state)) {
      sharedContext.resume().catch(error => console.warn('[SOUND] Audio resume failed:', error));
    }
  } catch (error) { console.warn('[SOUND] Audio initialization failed:', error); }
}


export function isSoundEnabled() {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(SOUND_SETTING_KEY) !== 'false';
}

export function setSoundEnabled(enabled) {
  if(!enabled&&typeof window!=='undefined')window.dispatchEvent(new Event('kelimo-result-audio-stop'));
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(SOUND_SETTING_KEY, enabled ? 'true' : 'false');
}

export function getSoundVolume() {
  if (typeof window === 'undefined') return 0.7;
  const stored = Number(window.localStorage.getItem(SOUND_VOLUME_KEY));
  return window.localStorage.getItem(SOUND_VOLUME_KEY) === null || !Number.isFinite(stored)
    ? 0.7 : Math.min(1, Math.max(0, stored));
}

export function setSoundVolume(volume) {
  if(typeof window!=='undefined')window.dispatchEvent(new Event('kelimo-result-audio-stop'));
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(SOUND_VOLUME_KEY, String(Math.min(1, Math.max(0, Number(volume) || 0))));
}

function tone(ctx, frequency, start, duration, gain = 0.045, type = 'sine') {
  const oscillator = ctx.createOscillator();
  const volume = ctx.createGain();
  oscillator.type = type;
  oscillator.frequency.setValueAtTime(frequency, start);
  volume.gain.setValueAtTime(0.0001, start);
  volume.gain.exponentialRampToValueAtTime(gain, start + 0.012);
  volume.gain.exponentialRampToValueAtTime(0.0001, start + duration);
  oscillator.connect(volume);
  volume.connect(ctx.destination);
  oscillator.start(start);
  oscillator.stop(start + duration + 0.02);
}

export function playSoundEffect(name) {
  if (!isSoundEnabled() || typeof window === 'undefined') return;
  if (name === 'error') {
    const now = Date.now();
    if (now - lastErrorSoundAt < 250) return;
    lastErrorSoundAt = now;
  }
  const volume = getSoundVolume();
  if (volume === 0) return;
  const AudioContext = window.AudioContext || window.webkitAudioContext;
  if (!AudioContext) return;

  try {
    if (!sharedContext || sharedContext.state === 'closed') sharedContext = new AudioContext();
    const ctx = sharedContext;
    const patterns = {
      // Confirmation is deliberately one short click-like tone. Turn uses a
      // longer three-note rise so simultaneous devices remain distinguishable.
      wheelTick: [[1100,0,.025],[420,.008,.045]],
      purchase: [[523,0,.12],[784,.12,.18],[1047,.26,.32]],
      equip: [[659,0,.10],[988,.12,.22]],
      confirm: [[920, 0, 0.055]],
      invite: [[620, 0, 0.09], [820, 0.11, 0.12]],
      turn: [[392, 0, 0.11], [587, 0.13, 0.12], [784, 0.27, 0.18]],
      win: [[523, 0, 0.18], [659, 0.15, 0.18], [784, 0.30, 0.20], [1047, 0.50, 0.65], [659, 0.50, 0.60], [784, 0.50, 0.60], [1319, 0.72, 0.36]],
      loss: [[392, 0, 0.24], [330, 0.23, 0.25], [262, 0.48, 0.50], [196, 0.48, 0.50]],
      pass: [[440, 0, 0.085], [330, 0.10, 0.12]],
      draw: [[523, 0, 0.12], [523, 0.16, 0.12]],
      place: [[680, 0, 0.045]],
      error: [[280, 0, 0.11], [220, 0.13, 0.15]],
      friend: [[660, 0, 0.07], [880, 0.09, 0.10]],
      accepted: [[587, 0, 0.09], [784, 0.11, 0.10], [988, 0.23, 0.16]],
      timeWarning: [[740, 0, 0.13], [740, 0.19, 0.13]]
    };
    const pattern = patterns[name];
    if (!pattern) {
      return;
    }
    const schedule=()=>{
      if (ctx.state !== 'running' || !isSoundEnabled() || getSoundVolume() === 0) return;
      const now = ctx.currentTime + 0.02;
      console.info('[SOUND] Playing:', name);
      const result = name === 'win' || name === 'loss';
      pattern.forEach(([frequency, offset, duration]) => tone(ctx, frequency, now + offset, duration, (result ? 0.075 : 0.045) * getSoundVolume(), result ? 'triangle' : 'sine'));
    };
    // Schedule after resume; never queue a stale celebration for a later gesture.
    if(['suspended','interrupted'].includes(ctx.state)){
      const requestedAt=Date.now();
      ctx.resume().then(()=>{if(Date.now()-requestedAt<1500)schedule();}).catch(error=>console.warn('[SOUND] Audio resume failed:',error));
    }else schedule();
    // Keep context alive for later turn events that arrive without a user gesture.
  } catch (error) {
    console.warn('[SOUND] Playback failed:', error);
    // Audio is progressive enhancement; gameplay must never depend on it.
  }
}

export function resultAudioContext(){
 if(!isSoundEnabled()||getSoundVolume()===0||typeof window==='undefined')return null;
 const AC=window.AudioContext||window.webkitAudioContext;if(!AC)return null;
 if(!sharedContext||sharedContext.state==='closed')sharedContext=new AC();return sharedContext;
}
